// Shared types for incentives processing

export interface IncentivesPayload {
  logicalStates: IncentivesState[];
}

export interface IncentivesState {
  logicalState: string;
  totalIncentives: number;
  incentives: IncentivesRecord[];
}

export interface IncentiveContent {
  applicableBodyStyle?: string;
  applicableModel?: string;
  applicableTrim?: string;
  content?: string;
  disclaimer?: string;
  incentiveId?: string;
  languageCode?: string;
  offerDetails?: string;
  shortTitle?: string;
  tcdHeadline?: string;
  tcdKeywords?: string;
  tcdLine1?: string;
  tcdLine2?: string;
  title?: string;
}

export interface IncentiveAccountContent {
  incentiveAccountMapId?: string;
  languageCode?: string;
  shortTitle?: string;
}

export interface IncentiveAccountMap {
  accountId?: string;
  dealerStackOption?: boolean;
  distributionListIdList?: string[];
  generateDisplayAds?: boolean;
  generateTcdAds?: boolean;
  incentiveId?: string;
  languageContentMap?: Record<string, IncentiveAccountContent>;
  status?: string;
  statusString?: string;
}

export interface Vehicle {
  globalVehicleId: string;
  newGlobalVehicleId?: boolean;
  year: number;
  make: string;
  model?: string;
  bodyStyle?: string;
  languageCd?: string;
  countryCd?: string;
  vehicleDescription?: any;
  vehicleEquipment?: any;
  vehicleExteriorColors?: any;
  vehicleFeatures?: any;
  vehicleInteriorColors?: any;
  vehicleReview?: any;
  vehicleMedia?: any;
  vehicleColors?: any;
}

export interface IncentivesRecord {
  accountMap: IncentiveAccountMap;
  acquisitionFee?: number;
  applicableCondition: string;
  applicableMake: string;
  applicableMakeCode?: string;
  applicableVehicleList: Vehicle[];
  applicableYear: number;
  bestApr?: number;
  bestInstallmentDetail?: any;
  bestTermMonths?: number;
  capitalizationCost?: number;
  cashDue: number;
  cashOption: number;
  clonedFromIncentiveId?: string;
  complianceAccountId?: string;
  complianceAccountName?: string;
  conditional: boolean;
  conditionalCategory?: string;
  content: IncentiveContent;
  countryCode?: string;
  created: number;
  creatorAccountId: string;
  creatorAccountName: string;
  customApr: number;
  customTermMonths?: number;
  distinctAprs?: number[];
  distinctTerms?: number[];
  effectiveDate: number;
  exclusive?: boolean;
  expirationDate: number;
  featured: boolean;
  fortyEightMonthApr: number;
  fortyTwoMonthApr: number;
  generateDisplayAds?: boolean;
  generateTcdAds?: boolean;
  globalVehicleTrimIdList?: string[];
  id?: string;
  imageUrl?: string;
  incentiveInstallmentDetails?: unknown;
  languageContentMap?: Record<string, IncentiveContent>;
  lenderName?: string;
  mappedByZipCode?: boolean;
  mileOverageCharge?: number;
  mileageAllowance?: number;
  minPrice?: number;
  monthlyPayment?: number;
  msrp?: number;
  purchaseOptionPrice?: number;
  securityDeposit?: number;
  seventyTwoMonthApr: number;
  sixtyMonthApr: number;
  temporalState?: string;
  thirdPartyIncentive?: boolean;
  thirdPartyIncentiveId?: string;
  thirtyNineMonthApr: number;
  thirtySixMonthApr: number;
  tokenizedFieldsMap?: any;
  twentyFourMonthApr: number;
  type: string;
  typeString?: string;
  vinList?: string[];
  zipCodes?: string[];
  extractedShortTitle?: string; // surfaced from combining accountMapContent and content
}

export interface RawIncentivesState {
  logicalState: string;
  totalIncentives: number;
  incentives: IncentivesRecord[];
}

export interface RawIncentivesPayload {
  valueObjectList: RawIncentivesState[];
}

export const orderedLogicalStates = [
  "DRAFTS",
  "ACTION_REQUIRED",
  "UNDER_COMPLIANCE_REVIEW",
  "PENDING_PUBLICATION",
  "PUBLISHED",
  "REJECTED",
  "EXPIRED"
];

// HTTP Error class for shared error handling
export class HttpError extends Error {
  statusCode: number;
  message: string;
  constructor(statusCode: number, message: string) {
    super();
    this.name = "HttpError";
    this.statusCode = statusCode;
    this.message = message;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

export function processIncentivesRecord(
  incentive: IncentivesRecord,
  currentAccountId: string,
  currentLang: string
): IncentivesRecord {
  return {
    ...incentive,
    conditional: incentive.conditional || false,
    featured: incentive.featured || false,
    cashOption: incentive.cashOption || 0,
    twentyFourMonthApr: incentive.twentyFourMonthApr || -1.0,
    thirtySixMonthApr: incentive.thirtySixMonthApr || -1.0,
    thirtyNineMonthApr: incentive.thirtyNineMonthApr || -1.0,
    fortyTwoMonthApr: incentive.fortyTwoMonthApr || -1.0,
    fortyEightMonthApr: incentive.fortyEightMonthApr || -1.0,
    sixtyMonthApr: incentive.sixtyMonthApr || -1.0,
    seventyTwoMonthApr: incentive.seventyTwoMonthApr || -1.0,
    customTermMonths: incentive.customTermMonths || -1,
    customApr: incentive.customApr || -1.0,
    msrp: incentive.msrp || -1.0,
    cashDue: incentive.cashDue || -1.0,
    purchaseOptionPrice: incentive.purchaseOptionPrice || -1.0,
    vinList: incentive.vinList || [],
    typeString: incentive.typeString || incentive.type,
    applicableYear: incentive.applicableYear || -1,
    globalVehicleTrimIdList: incentive.globalVehicleTrimIdList || [],
    complianceAccountName: incentive.complianceAccountName,
    monthlyPayment: incentive.monthlyPayment || -1,
    mileageAllowance: incentive.mileageAllowance || -1,
    mileOverageCharge: incentive.mileOverageCharge || -1,
    extractedShortTitle:
      extractShortTitle(incentive, currentAccountId, currentLang) || ""
  };
}

// the following pair of functions is for managing the shortTitle field, which is displayed and
// edited in different ways depending on whether we are in creatorAccountId == currentAccountId
// situation.  If the incentive was created by the current account, then the shortTitle will
// be the one in the incentive.content element, otherwise it will be found nested inside
// the accountMap.  The purpose of this is to allow different child accounts to have different
// shortTtile values for the same incentive
// We pull the correct shortTitle out of the incentive and store it in extractedShortTitle,
// where it is used for editing and viewing
export function extractShortTitle(
  incentive: IncentivesRecord,
  currentAccountId: string,
  currentLang: string
): string | undefined {
  if (incentive.creatorAccountId === currentAccountId) {
    return incentive.content?.shortTitle;
  } else {
    return (
      incentive.accountMap?.languageContentMap?.[currentLang]?.shortTitle ??
      incentive.content?.shortTitle
    );
  }
}

export function unextractShortTitle(
  incentive: IncentivesRecord,
  currentAccountId: string,
  currentLang: string
): IncentivesRecord {
  const newContent = {
    ...incentive.content,
    shortTitle: incentive.extractedShortTitle
  };
  if (currentAccountId === incentive.creatorAccountId) {
    return {
      ...incentive,
      content: newContent,
      languageContentMap: {
        [currentLang]: newContent
      }
    };
  } else {
    return {
      ...incentive,
      accountMap: {
        languageContentMap: {
          [currentLang]: newContent
        }
      },
      content: newContent,
      languageContentMap: {
        [currentLang]: newContent
      }
    };
  }
}
